import { useSelector } from 'react-redux'
import moment from 'moment'
import { IRootReduxState } from 'types/reducers'

const DATE_FORMAT_MAP: Record<string, string> = {
  'MM/DD/YYYY': 'MM/DD/YYYY',
  'DD/MM/YYYY': 'DD/MM/YYYY',
  'YYYY-MM-DD': 'YYYY-MM-DD',
  'DD.MM.YYYY': 'DD.MM.YYYY',
}

const TIME_FORMAT_MAP: Record<string, string> = {
  '12-hour': 'hh:mm A',
  '24-hour': 'HH:mm',
}

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY'
const DEFAULT_TIME_FORMAT = '12-hour'

export const useDateTimeFormat = () => {
  const dateFormatPref = useSelector(
    (state: IRootReduxState) => state.app.user.dateFormat ?? DEFAULT_DATE_FORMAT
  )
  const timeFormatPref = useSelector(
    (state: IRootReduxState) => state.app.user.timeFormat ?? DEFAULT_TIME_FORMAT
  )

  const dateFmt = DATE_FORMAT_MAP[dateFormatPref] ?? DATE_FORMAT_MAP[DEFAULT_DATE_FORMAT]
  const timeFmt = TIME_FORMAT_MAP[timeFormatPref] ?? TIME_FORMAT_MAP[DEFAULT_TIME_FORMAT]
  const dateTimeFmt = `${dateFmt} ${timeFmt}`

  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return ''
    const m = moment(iso)
    return m.isValid() ? m.format(dateFmt) : ''
  }

  // For calendar-date-only values (e.g. a certification's Certified/Expiry date)
  // that have no real time-of-day — these are typically stored as UTC midnight
  // ("2026-08-27T00:00:00Z") purely as a date-only convention, not an actual
  // instant in time. Formatting that through local-timezone conversion (like
  // formatDate does) rolls the calendar day back by one for any viewer in a
  // timezone behind UTC. This formats using the date's own UTC components
  // instead, so the calendar day never shifts regardless of viewer timezone.
  const formatDateOnly = (dateOnly: string | null | undefined): string => {
    if (!dateOnly) return ''
    const m = moment.utc(dateOnly)
    return m.isValid() ? m.format(dateFmt) : ''
  }

  const formatTime = (iso: string | null | undefined): string => {
    if (!iso) return ''
    const m = moment(iso)
    return m.isValid() ? m.format(timeFmt) : ''
  }

  const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return ''
    const m = moment(iso)
    return m.isValid() ? m.format(dateTimeFmt) : ''
  }

  return { formatDate, formatDateOnly, formatTime, formatDateTime, dateFmt, timeFmt, dateTimeFmt }
}
